const API_BASE = (process.env.REACT_APP_API_BASE || '/api').replace(/\/$/, '');
export const SIGNUP_COMPLETED_EVENT = 'signup-completed';

// Session identity is not sent as a body: only the server can verify confirmation
// and decide whether this is a genuinely new signup. Its durable ledger owns dedupe.
export const createSignupCompletionNotifier = () => {
  const attempts = new Map();
  let currentSession = null;
  let timer;
  let controller;
  let disposed = false;

  const cancelPending = () => {
    clearTimeout(timer);
    controller?.abort();
    currentSession = null;
  };

  const run = async () => {
    const session = currentSession;
    if (disposed || !session) return;
    const id = session.user.id;
    const count = (attempts.get(id) || 0) + 1;
    attempts.set(id, count);
    const requestController = new AbortController();
    controller = requestController;
    const timeout = setTimeout(() => requestController.abort(), 10000);
    let retry = false;
    try {
      const response = await fetch(`${API_BASE}/notifications/auth/signup/complete`, {
        method: 'POST',
        mode: 'cors',
        credentials: 'omit',
        headers: { Authorization: `Bearer ${session.access_token}` },
        signal: requestController.signal,
      });
      retry = response.status === 429 || response.status >= 500;
      if (response.ok && !disposed && currentSession?.user.id === id) {
        window.dispatchEvent(new CustomEvent(SIGNUP_COMPLETED_EVENT, { detail: { userId: id } }));
      }
      if (!retry) attempts.set(id, 2);
    } catch (_) {
      retry = true;
    } finally {
      clearTimeout(timeout);
      if (controller === requestController) controller = null;
    }
    // One retry only; auth events cannot reset the budget or create a request loop.
    if (retry && count < 2 && !disposed && currentSession?.user.id === id) {
      timer = setTimeout(run, 1000);
    }
  };

  return {
    schedule(session) {
      if (disposed) return;
      if (!session?.access_token || !session?.user?.id) {
        cancelPending();
        return;
      }
      if (currentSession?.user.id === session.user.id) {
        currentSession = session;
        return;
      }
      cancelPending();
      currentSession = session;
      if (attempts.has(session.user.id)) return;
      // Leave the Supabase auth listener/lock before doing any work.
      timer = setTimeout(run, 0);
    },
    dispose() {
      disposed = true;
      cancelPending();
    },
  };
};
